// Field transformation functions for backward compatibility

/**
 * Transform treatment data fields
 * @param {Object} data - Treatment data
 * @returns {Object} Transformed data
 */
export const transformTreatmentData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  if (transformed.title !== undefined) {
    transformed.name = transformed.title;
    delete transformed.title;
  }
  if (transformed.price_min !== undefined) {
    transformed.cost_estimate_min = transformed.price_min;
    delete transformed.price_min;
  }
  if (transformed.price_max !== undefined) {
    transformed.cost_estimate_max = transformed.price_max;
    delete transformed.price_max;
  }
  // Remove status field - it doesn't exist in DB
  if (transformed.status !== undefined) {
    delete transformed.status;
  }

  return transformed;
};

/**
 * Transform inquiry data fields
 * @param {Object} data - Inquiry data
 * @returns {Object} Transformed data
 */
export const transformInquiryData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  if (transformed.full_name !== undefined) {
    transformed.patient_name = transformed.full_name;
    delete transformed.full_name;
  }
  if (transformed.email !== undefined) {
    transformed.patient_email = transformed.email;
    delete transformed.email;
  }
  if (transformed.phone_number !== undefined) {
    transformed.phone = transformed.phone_number;
    delete transformed.phone_number;
  }
  if (transformed.inquiry_type !== undefined) {
    transformed.treatment_interest = transformed.inquiry_type;
    delete transformed.inquiry_type;
  }
  // Note: 'status' field exists in DB as-is, no transformation needed

  return transformed;
};

/**
 * Transform blog post data fields
 * @param {Object} data - Blog post data
 * @returns {Object} Transformed data
 */
export const transformBlogPostData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  // Remove slug field - it doesn't exist in DB
  if (transformed.slug !== undefined) {
    delete transformed.slug;
  }
  // Convert status string to published boolean
  if (transformed.status !== undefined) {
    transformed.published = transformed.status === 'published' || transformed.status === true;
    delete transformed.status;
  }

  return transformed;
};

/**
 * Transform medical procedure data fields
 * @param {Object} data - Medical procedure data
 * @returns {Object} Transformed data
 */
export const transformMedicalProcedureData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  if (transformed.description !== undefined) {
    transformed.general_description = transformed.description;
    delete transformed.description;
  }

  return transformed;
};

/**
 * Transform image SEO metadata fields
 * @param {Object} data - Image SEO metadata
 * @returns {Object} Transformed data
 */
export const transformImageSEOData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  if (transformed.title !== undefined) {
    transformed.title_text = transformed.title;
    delete transformed.title;
  }
  if (transformed.image_id !== undefined) {
    // image_id doesn't exist in schema - remove it
    delete transformed.image_id;
  }

  return transformed;
};

/**
 * Transform page SEO data fields
 * @param {Object} data - Page SEO data
 * @returns {Object} Transformed data
 */
export const transformPageSEOData = (data: unknown) => {
  const transformed = { ...(data as Record<string, unknown>) };

  // Ensure meta_keywords is an array
  if (transformed.meta_keywords !== undefined) {
    if (typeof transformed.meta_keywords === 'string') {
      // Convert comma-separated string to array
      transformed.meta_keywords = transformed.meta_keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
    } else if (!Array.isArray(transformed.meta_keywords)) {
      transformed.meta_keywords = [];
    }
  }

  return transformed;
};

// Helper function to check if a key is dangerous
const isDangerousKey = (key: string | null | undefined): boolean => {
  return !key || key === '__proto__' || key === 'constructor' || key === 'prototype';
};

// Helper function to get transformed key from field map
const getTransformedKey = (key: string, fieldMap: Record<string, string | null>): string => {
  // Validate key before accessing fieldMap to prevent object injection
  if (isDangerousKey(key)) {
    return key;
  }

  if (Object.prototype.hasOwnProperty.call(fieldMap, key)) {
    const mappedValue = Reflect.get(fieldMap, key);
    return mappedValue ?? key;
  }
  return key;
};

// Helper function to validate filter entry
const isValidFilterEntry = (key: string, value: unknown): boolean => {
  return value !== undefined && value !== null && typeof key === 'string';
};

/**
 * Transform filter keys for backward compatibility
 * @param {Object} filters - Original filters
 * @param {Object} fieldMap - Map of old field names to new field names
 * @returns {Object} Transformed filters
 */
export const transformFilterKeys = (
  filters: Record<string, unknown> | null | undefined,
  fieldMap: Record<string, string | null> = {}
) => {
  if (!filters) return {};

  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (!isValidFilterEntry(key, value)) continue;

    const transformedKey = getTransformedKey(key, fieldMap);

    if (isDangerousKey(transformedKey)) continue;

    // Safe property assignment
    Object.defineProperty(transformed, transformedKey, {
      value,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  }

  return transformed;
};
